/**
 * SmokeLayer Component
 *
 * Displays smoke/aerosol coverage using NASA GIBS (Global Imagery Browse Services).
 * Uses MODIS Aerosol Optical Depth layers from both Terra (morning) and Aqua (afternoon)
 * satellites to provide better coverage.
 *
 * Data source: NASA Worldview / GIBS
 * Update frequency: ~1 day delay for processed products
 * Coverage: Global (combined Terra + Aqua fills ~40% more gaps)
 * Cost: Free (served directly from NASA)
 */

import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

interface SmokeLayerProps {
  enabled?: boolean;
  opacity?: number;
}

// NASA GIBS WMS endpoint
const GIBS_WMS_URL = 'https://gibs.earthdata.nasa.gov/wms/epsg3857/best/wms.cgi';

// MODIS Aerosol Optical Depth layers - both satellites for better coverage
const SMOKE_LAYERS = {
  // Terra satellite - morning pass (~10:30am local time)
  aod_terra: 'MODIS_Terra_Aerosol_Optical_Depth_3km',
  // Aqua satellite - afternoon pass (~1:30pm local time)
  aod_aqua: 'MODIS_Aqua_Aerosol_Optical_Depth_3km',
};

/**
 * Get yesterday's date in YYYY-MM-DD format.
 * NASA GIBS data has ~1 day delay for processed products.
 */
function getGIBSDate(): string {
  const date = new Date();
  // Go back 1 day to ensure data is available
  date.setDate(date.getDate() - 1);
  return date.toISOString().split('T')[0];
}

/**
 * SmokeLayer - displays smoke/aerosol coverage from NASA GIBS.
 * Combines Terra and Aqua satellite passes for better spatial coverage.
 * Uses WMS tiles served directly from NASA (no AWS costs).
 */
export default function SmokeLayer({ enabled = true, opacity = 0.6 }: SmokeLayerProps) {
  const map = useMap();
  const terraLayerRef = useRef<L.TileLayer.WMS | null>(null);
  const aquaLayerRef = useRef<L.TileLayer.WMS | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      // Remove layers when disabled
      if (terraLayerRef.current) {
        map.removeLayer(terraLayerRef.current);
        terraLayerRef.current = null;
      }
      if (aquaLayerRef.current) {
        map.removeLayer(aquaLayerRef.current);
        aquaLayerRef.current = null;
      }
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const gibsDate = getGIBSDate();

      // Create Terra layer (morning pass)
      const terraLayer = L.tileLayer.wms(GIBS_WMS_URL, {
        layers: SMOKE_LAYERS.aod_terra,
        format: 'image/png',
        transparent: true,
        opacity: opacity,
        // @ts-expect-error - L.tileLayer.wms accepts additional parameters
        time: gibsDate,
        crs: L.CRS.EPSG3857,
        attribution: 'Smoke: <a href="https://worldview.earthdata.nasa.gov" target="_blank">NASA MODIS</a>',
      });

      // Create Aqua layer (afternoon pass)
      const aquaLayer = L.tileLayer.wms(GIBS_WMS_URL, {
        layers: SMOKE_LAYERS.aod_aqua,
        format: 'image/png',
        transparent: true,
        opacity: opacity,
        // @ts-expect-error - L.tileLayer.wms accepts additional parameters
        time: gibsDate,
        crs: L.CRS.EPSG3857,
      });

      // Add both layers - they'll overlay and fill each other's gaps
      terraLayer.addTo(map);
      aquaLayer.addTo(map);

      terraLayerRef.current = terraLayer;
      aquaLayerRef.current = aquaLayer;

      // Track loading state
      let loadingCount = 2;
      const onLoad = () => {
        loadingCount--;
        if (loadingCount === 0) setIsLoading(false);
      };

      terraLayer.on('load', onLoad);
      aquaLayer.on('load', onLoad);
      terraLayer.on('tileerror', onLoad);
      aquaLayer.on('tileerror', onLoad);

      console.log(`[SmokeLayer] Added Terra + Aqua aerosol layers for ${gibsDate}`);

    } catch (err) {
      console.error('[SmokeLayer] Error adding smoke layers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load smoke data');
      setIsLoading(false);
    }

    // Cleanup on unmount or when disabled
    return () => {
      if (terraLayerRef.current) {
        map.removeLayer(terraLayerRef.current);
        terraLayerRef.current = null;
      }
      if (aquaLayerRef.current) {
        map.removeLayer(aquaLayerRef.current);
        aquaLayerRef.current = null;
      }
    };
  }, [enabled, map, opacity]);

  // Update opacity when it changes
  useEffect(() => {
    if (terraLayerRef.current) {
      terraLayerRef.current.setOpacity(opacity);
    }
    if (aquaLayerRef.current) {
      aquaLayerRef.current.setOpacity(opacity);
    }
  }, [opacity]);

  // Log state for debugging
  if (error) {
    console.warn('[SmokeLayer] Error state:', error);
  }
  if (isLoading) {
    console.log('[SmokeLayer] Loading...');
  }

  return null;
}
